import { useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Grid, TextField, Typography, Autocomplete } from "@mui/material";
import { endpoints } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import DropzoneComponent from "../DropzoneComponent";
import { FormsIds } from "../../Utils/Constants";
import {
  ExamQuestionModel,
  ExamQuestionValidationSchema,
} from "../../Utils/Models";
import { ExamQuestionFormProps } from "../../Utils/Types";
import ExamAnswersField from "./ExamAnswersField";
import { isDefined } from "../../Utils/Utils";

export default function ExamQuestionForm({
  onClose,
  selectedRow,
  examSets,
}: ExamQuestionFormProps) {
  const { control, handleSubmit, reset, setValue } = useForm<any>({
    resolver: yupResolver(ExamQuestionValidationSchema),
    defaultValues: selectedRow || {
      answers: [{ text: "", isCorrect: false }],
      examSetId: "",
      questionText: "",
      imageUrl: "",
      explanation: "",
    },
  });

  useEffect(() => {
    reset(
      selectedRow || {
        answers: [{ text: "", isCorrect: false }],
        examSetId: "",
        questionText: "",
        imageUrl: "",
        explanation: "",
      }
    );
  }, [selectedRow, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "answers",
  });

  const formatData = (model: ExamQuestionModel) => {
    const formData = new FormData();
    Object.keys(model).forEach((key) => {
      const value = (model as any)[key];

      if (Array.isArray(value) && value[value.length - 1] instanceof File) {
        value.forEach((file: File) => {
          formData.append(key, file);
        });
      } else if (Array.isArray(value) && value.length > 0) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    return formData;
  };

  const createElement = async (data: ExamQuestionModel) => {
    try {
      const formData = formatData(data);
      const url = `${endpoints.examQuestions}`;
      const response = await postData(url, formData);
      if (!response) return;
      if (onClose) onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const updateElement = async (data: ExamQuestionModel) => {
    try {
      const formData = formatData(data);
      const url = `${endpoints.examQuestions}/${data._id}`;
      const response = await updateData(url, formData);
      if (!response) return;
      if (onClose) onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const onSubmit = async (data: ExamQuestionModel) => {
    data?._id ? updateElement(data) : createElement(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} id={FormsIds.examQuestionForm}>
      <Grid container spacing={2}>
        {/* Exam Set select */}
        <Grid item xs={12}>
          <Controller
            name="examSetId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                options={examSets || []}
                getOptionLabel={(option) =>
                  option?.name
                    ? `${option.name}${
                        isDefined(option.examCategory)
                          ? ` (Categoria ${option.examCategory})`
                          : ""
                      }`
                    : ""
                }
                value={examSets.find((set) => set._id === field.value) || null}
                onChange={(_, newValue) => field.onChange(newValue?._id || "")}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Vocabulary.examSet}
                    fullWidth
                    margin="normal"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />
        </Grid>
        {/* Question text */}
        <Grid item xs={12}>
          <Controller
            name="questionText"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={Vocabulary.question}
                fullWidth
                margin="normal"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        {/* Image upload */}
        <Grid item xs={12}>
          <Typography variant="h6">{Vocabulary.mainImage}</Typography>
          <Controller
            name="imageUrl"
            control={control}
            render={({ field }) => (
              <DropzoneComponent
                onSave={(e) => field.onChange(e)}
                maxFiles={1}
                paths={field.value ? [field.value] : []}
                altText={Vocabulary.mainImage}
              />
            )}
          />
        </Grid>
        {/* Answers */}
        <Grid item xs={12}>
          <ExamAnswersField
            control={control}
            setValue={setValue}
            fields={fields}
            append={append}
            remove={remove}
          />
        </Grid>
        {/* Explanation */}
        <Grid item xs={12}>
          <Controller
            name="explanation"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={Vocabulary.explanation || "Explicație"}
                fullWidth
                margin="normal"
                multiline
                minRows={2}
              />
            )}
          />
        </Grid>
      </Grid>
    </form>
  );
}
